import type { Chat } from './api'
import { laneStatusFriendly } from './laneStatus'

// `hasPendingOutbox` marks a chat with an undelivered message in the local
// outbox. The server cannot see that work (no queued row exists yet), so the
// caller supplies it: the chat is live and a lane holding one is not free.
// Unread replies also stay in Done while work continues in Live.
export function matchesDoneChatFilter(chat: Chat, hasPendingOutbox: boolean = false): boolean {
  if (isSnoozedChat(chat)) return false
  if (chat.pending_approval) return true
  if ((chat.unread ?? 0) > 0) return true
  if (hasActiveChatWork(chat, hasPendingOutbox)) return false
  if (chat.awaiting_answer) return true
  if (isSnoozedChat(chat)) return false
  const latestResetAt = chat.last_reset_at ?? 0
  const latestAgentReplyAt = chat.last_agent_reply_at ?? 0
  const latestMessageIsAgentReply =
    chat.last_message_direction === 'outbound' || chat.last_message_direction === 'agent'
  return (
    !hasActiveChatWork(chat, hasPendingOutbox)
    && latestAgentReplyAt > latestResetAt
    && latestMessageIsAgentReply
  )
}

export function hasActiveChatWork(chat: Chat, hasPendingOutbox: boolean = false): boolean {
  // Messages held behind an unanswered question card wait on Mark, not the agent.
  const heldForAnswer = chat.awaiting_answer === true
  return hasPendingOutbox
    || (chat.side_job_work_depth ?? 0) > 0
    || (!heldForAnswer && (
      chat.lane_status === 'busy'
      || chat.active_work_depth > 0
      || (chat.queue_depth ?? 0) > 0
    ))
    || (chat.scheduled_rechecks?.length ?? 0) > 0
}

export function isSnoozedChat(chat: Chat): boolean {
  return chat.type !== 'auto-spawn'
    && !!chat.snoozed_until
    && chat.snoozed_until > Math.floor(Date.now() / 1000)
}

export function isFreeChat(chat: Chat, liveStream: boolean = true, hasPendingOutbox: boolean = false): boolean {
  const latestResetAt = chat.last_reset_at ?? 0
  if (chat.type === 'auto-spawn') return false
  if (isSnoozedChat(chat)) return false
  if (hasPendingOutbox) return false

  const latestAgentReplyAt = chat.last_agent_reply_at ?? 0
  const latestActivityAt = chat.last_activity_at ?? 0
  const laneStatus = laneStatusFriendly(chat.lane_status, chat.lane_last_heartbeat, {}, liveStream)
  // A synthetic list-sort timestamp is not evidence that Mark used a lane.
  // Newly introduced lanes can be surfaced near the top before they have any
  // transcript rows; no reset, reply, or message direction is the durable
  // never-used signal.
  const hasNeverBeenUsed =
    latestResetAt <= 0
    && latestAgentReplyAt <= 0
    && chat.last_message_direction == null

  return (
    laneStatus.tone === 'green'
    && !hasActiveChatWork(chat)
    && (
      hasNeverBeenUsed
      || (
        latestResetAt > 0
        && latestAgentReplyAt <= latestResetAt
        && latestActivityAt <= latestResetAt
      )
    )
  )
}
